import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ServiceStep = ({ data, onUpdate, onNext, onBack }) => {
  const [selectedService, setSelectedService] = useState(data?.service || null);
  const [selectedUrgency, setSelectedUrgency] = useState(data?.urgency || null);
  const [error, setError] = useState('');
  const [showPricing, setShowPricing] = useState(!!data?.service);

  const services = [
    {
      id: 'debouchage-standard',
      name: 'Débouchage',
      description: 'Débouchage mécanique d\'évacuation (lavabo, douche, baignoire)',
      basePrice: 89,
      duration: '30-45 min',
      type: 'fixed',
      icon: 'Wrench',
      features: ['Intervention rapide', 'Garantie 6 mois', 'Matériel professionnel'],
    },
    {
      id: 'reparation-fuite',
      name: 'Réparation de fuite',
      description: 'Réparation de fuite visible (robinet, joint, raccord)',
      basePrice: 120,
      duration: '1h',
      type: 'fixed',
      icon: 'Droplet',
      features: ['Diagnostic inclus', 'Pièces standards incluses', 'Test d\'étanchéité'],
    },
    {
      id: 'remplacement-robinet',
      name: 'Remplacement robinetterie',
      description: 'Dépose et pose d\'un robinet (fourni par le client)',
      basePrice: 95,
      duration: '45 min',
      type: 'fixed',
      icon: 'Settings',
      features: ['Installation professionnelle', 'Test d\'étanchéité', 'Garantie pose'],
    },
    {
      id: 'installation-equipement',
      name: 'Installation équipement',
      description: 'Installation de chauffe-eau, chaudière, ou équipement complexe',
      basePrice: 0,
      priceRange: { min: 300, max: 1500 },
      duration: 'Variable',
      type: 'quote',
      icon: 'Package',
      features: ['Étude technique', 'Devis personnalisé', 'Installation certifiée'],
    },
    {
      id: 'diagnostic-complet',
      name: 'Diagnostic approfondi',
      description: 'Inspection complète avec rapport détaillé et recommandations',
      basePrice: 0,
      priceRange: { min: 150, max: 500 },
      duration: '30-60 min',
      type: 'quote',
      icon: 'Search',
      features: ['Diagnostic gratuit', 'Devis détaillé', 'Conseils personnalisés'],
    },
  ];

  const urgencyOptions = [
    {
      id: 'standard',
      label: 'Standard',
      name: 'Intervention standard',
      description: 'Sous 48h - Jours ouvrés',
      icon: 'Calendar',
      priceMultiplier: 1,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'today',
      label: 'Aujourd\'hui',
      name: 'Intervention aujourd\'hui',
      description: 'Selon disponibilité',
      icon: 'Clock',
      priceMultiplier: 1.5,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      badge: '+50%'
    },
    {
      id: 'urgent',
      label: 'Urgence',
      name: 'Intervention urgente',
      description: 'Sous 2h - 24h/24, 7j/7',
      icon: 'AlertCircle',
      priceMultiplier: 2,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      badge: '+100%'
    }
  ];

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setShowPricing(true);
    setError('');
    // Reset urgency when changing service
    if (selectedService?.id !== service?.id) {
      setSelectedUrgency(null);
    }
  };

  const handleUrgencySelect = (urgency) => {
    setSelectedUrgency(urgency);
    setError('');
  };

  const handleSubmit = () => {
    if (!selectedService) {
      setError('Veuillez sélectionner un service');
      return;
    }
    if (!selectedUrgency) {
      setError('Veuillez sélectionner un créneau d\'intervention');
      return;
    }

    const finalPrice = selectedService?.type === 'fixed' 
      ? selectedService?.basePrice * selectedUrgency?.priceMultiplier 
      : 0;

    onUpdate({
      service: { ...selectedService, price: finalPrice },
      urgency: selectedUrgency
    });
    onNext(true);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="ClipboardList" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">Quelle prestation souhaitez-vous ?</h2>
            <p className="text-muted-foreground">
              Sélectionnez le service adapté à votre besoin
            </p>
          </div>
        </div>

        {/* Services Selection */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Services disponibles</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {services?.map((service) => (
                <button
                  key={service?.id}
                  onClick={() => handleServiceSelect(service)}
                  className={`text-left p-5 rounded-lg border-2 transition-all duration-200 ${
                    selectedService?.id === service?.id
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border hover:border-primary/50 bg-card'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          selectedService?.id === service?.id ? 'bg-primary/20' : 'bg-muted'
                        }`}
                      >
                        <Icon
                          name={service?.icon}
                          size={20}
                          color={selectedService?.id === service?.id ? 'var(--color-primary)' : 'currentColor'}
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">{service?.name}</h4>
                        <p className="text-xs text-muted-foreground">{service?.duration}</p>
                      </div>
                    </div>
                    {selectedService?.id === service?.id && (
                      <Icon name="CheckCircle" size={24} color="var(--color-primary)" />
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-3">{service?.description}</p>

                  <div className="space-y-1.5">
                    {service?.features?.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Icon name="Check" size={14} color="var(--color-success)" />
                        <span className="text-xs text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Pricing & Urgency Section - Only shown after service selection */}
          {showPricing && selectedService && (
            <div className="pt-6 border-t border-border animate-in fade-in slide-in-from-top-4 duration-500">
              <h3 className="text-lg font-medium text-foreground mb-4">Tarif et disponibilité</h3>
              
              {/* Price Display */}
              <div className="mb-6 p-5 bg-muted/30 rounded-lg">
                {selectedService?.type === 'fixed' ? (
                  <div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold text-foreground">{selectedService?.basePrice} €</span>
                      <span className="text-sm text-muted-foreground">TTC - Prix de base</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-success">
                      <Icon name="Lock" size={14} />
                      <span className="font-medium">Prix fixe garanti</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-warning/10 text-warning rounded-full text-sm font-medium mb-3">
                      <Icon name="FileText" size={16} />
                      <span>Sur devis personnalisé</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-foreground">
                        {selectedService?.priceRange?.min} € - {selectedService?.priceRange?.max} €
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Estimation selon complexité - Devis gratuit après diagnostic
                    </p>
                  </div>
                )}
              </div>

              {/* Urgency Options */}
              <div>
                <h4 className="text-base font-medium text-foreground mb-3">Choisissez votre créneau</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {urgencyOptions?.map((urgency) => {
                    const finalPrice = selectedService?.type === 'fixed' 
                      ? selectedService?.basePrice * urgency?.priceMultiplier 
                      : 0;

                    return (
                      <button
                        key={urgency?.id}
                        onClick={() => handleUrgencySelect(urgency)}
                        className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                          selectedUrgency?.id === urgency?.id
                            ? `${urgency?.borderColor} ${urgency?.bgColor} shadow-md`
                            : 'border-border hover:border-primary/50 bg-card'
                        }`}
                      >
                        {urgency?.badge && (
                          <div className={`absolute -top-2 -right-2 px-2 py-1 ${urgency?.bgColor} ${urgency?.color} text-xs font-bold rounded-full border-2 ${urgency?.borderColor}`}>
                            {urgency?.badge}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name={urgency?.icon} size={20} className={urgency?.color} />
                          <h5 className="font-semibold text-sm text-foreground">{urgency?.label}</h5>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">{urgency?.description}</p>
                        {selectedService?.type === 'fixed' && (
                          <div className="pt-3 border-t border-border">
                            <p className="text-lg font-bold text-foreground">{finalPrice?.toFixed(0)} € TTC</p>
                          </div>
                        )}
                        {selectedUrgency?.id === urgency?.id && (
                          <div className="absolute top-2 right-2">
                            <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-6 flex items-center gap-2 text-sm text-error bg-error/5 border border-error/20 rounded-lg p-4">
            <Icon name="AlertCircle" size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
          <Button variant="default" size="lg" iconName="ArrowRight" iconPosition="right" onClick={handleSubmit}>
            Continuer vers le paiement
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceStep;